import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AuthService } from '../services/auth.service';
import { AutorizacionService } from '../services/autorizacion.service';
import { TokenService } from '../services/token.service';
import { Autorizacion } from '../models/auth.models';

/**
 * Exige una sesión activa.
 *
 * Misma secuencia que Atlas: primero se comprueba el refresh token (local y,
 * una sola vez, contra el servidor) y solo entonces se carga el perfil.
 */
export const authGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const tokenService = inject(TokenService);
  const router = inject(Router);

  if (await tokenService.refreshTokenExpirado()) {
    authService.limpiar();
    return router.createUrlTree(['/login']);
  }

  const usuario = authService.usuario() ?? (await authService.cargarPerfil());
  if (!usuario) {
    authService.limpiar();
    return router.createUrlTree(['/login']);
  }

  // `acceso` es la bandera del backend que habilita el frontend.
  if (!usuario.acceso) {
    authService.limpiar();
    return router.createUrlTree(['/login']);
  }

  return true;
};

/** Impide volver al login con una sesión viva. */
export const invitadoGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const tokenService = inject(TokenService);
  const router = inject(Router);

  if (await tokenService.refreshTokenExpirado()) {
    authService.limpiar();
    return true;
  }

  const usuario = authService.usuario() ?? (await authService.cargarPerfil());
  return usuario ? router.createUrlTree(['/']) : true;
};

/**
 * Exige los permisos declarados en `data.autorizacion` de la ruta.
 *
 * Es solo una comodidad de UX: la autoridad real está en el backend, que valida
 * los mismos permisos en cada endpoint.
 */
export const autorizacionGuard: CanActivateFn = (route) => {
  const autorizacionService = inject(AutorizacionService);
  const router = inject(Router);

  const autorizacion = route.data['autorizacion'] as Autorizacion | undefined;
  if (!autorizacion) return true;

  return autorizacionService.tieneAutorizacion(autorizacion) ? true : router.createUrlTree(['/']);
};
